import useComingSoonModal from '@/Components/Common/ComingSoonModal/store';
import Header from '@/Features/App/Components/Header';
import { useMediaQuery, useTheme } from '@mui/material';
import useChatStore, {
  EVENT_NEW_CHAT,
  EVENT_SELECT_CHAT,
  REMOVE_MODEL,
  SCRIPT_FOR_TTS,
} from 'Components/Chat/store';
import ComingSoonModal from 'Components/Common/ComingSoonModal/ComingSoonModal';
import ModalMobileUG from 'Components/ModalMobileUG';
import { ChatProvider } from 'Context/Chat/Chat.context';
import { ChatMessagesProvider } from 'Context/Chat/ChatMessagesContext';
import MobilePowerPanel from 'Features/App/Components/MobilePowerPanel';
import PowerPanel from 'Features/App/Components/PowerPanel';
import SmartMergeDialog from 'Features/Core/Components/SmartMerge/SmartMergeDialog';
import Welcome from 'Features/Login/Welcome';
import useSmartMergeStore from 'Features/SmartMerge/store';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';

const AuthLayout = ({ store }) => {
  const outlet = useOutlet();
  const navigate = useNavigate();
  const location = useLocation();

  // Logout the user if change this state
  useEffect(() => {
    if (!store.isLoggedIn) {
      navigate('/');
    }
  }, [store.isLoggedIn]);

  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showModalTutos, setShowModalTutos] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const currentEvent = useChatStore((state) => state.currentEvent);
  const mobilePowerPanel = useChatStore((state) => state.mobilePowerPanel);
  const setMobilePowerPanel = useChatStore(
    (state) => state.setMobilePowerPanel
  );

  const active = useSmartMergeStore((state) => state.active);

  const [allModels, setAllModels] = useState([]);

  // Fetch all models first
  useEffect(() => {
    const fetchAllModels = async () => {
      try {
        const response = await store.api.get(`/model`);
        setAllModels(response.data.models || []);
      } catch (error) {
        console.error('Error fetching all models:', error);
      }
    };

    if (store) {
      fetchAllModels();
    }
  }, [store]);

  const sortByAttribute = (array, attribute) => {
    return array
      .slice()
      .sort((a, b) => a[attribute].localeCompare(b[attribute]));
  };

  const oldSelectedModels = React.useRef([]);
  const updateSelectedModels = (updateFn) => {
    useChatStore.getState().updateSelectedModels(updateFn);
  };
  const setEditMessChange = () => {};

  const setSelectedModels = (models) => {
    if (!models || models.length === 0) return;

    // Ensure models have all required fields, especially _id
    const validModels = models.filter((model) => model && model._id);

    if (validModels.length === 0) return;

    // Sort the models
    const sortedModels = sortByAttribute(validModels, 'abbreviation');

    // Update reference
    oldSelectedModels.current = sortedModels;

    // Use updateSelectedModels which will trigger proper verifyComboMatch
    updateSelectedModels(() => sortedModels);

    // Update localStorage for persistence
    localStorage.setItem('currentModels', JSON.stringify(sortedModels));

    setEditMessChange();
  };

  // Initialize combos when allModels is loaded
  useEffect(() => {
    const initCombos = async () => {
      if (!store || !Array.isArray(allModels) || allModels.length === 0) return;

      try {
        // Step 1: Check if user is new
        const isNewUserResponse = await store.api.get('/combos/is-new-user');
        const { isNewUser } = isNewUserResponse.data;

        let combos, selectedCombo, modelIds;

        // Tracking variable to avoid duplicate updates
        let endpointCalled = false;

        if (isNewUser) {
          // Step 2: Create default combos for new user
          endpointCalled = true;
          const createResponse = await store.api.post(
            '/combos/create-default-combos'
          );

          // Validate response
          if (!createResponse?.data) {
            throw new Error(
              'Invalid response from create-default-combos endpoint'
            );
          }

          combos = Array.isArray(createResponse.data.combos)
            ? createResponse.data.combos
            : [];
          selectedCombo = createResponse.data.selectedCombo || null;
          modelIds = selectedCombo?.llms || [];
        } else {
          // Step 3: Get active combo for existing user
          endpointCalled = true;
          const activeComboResponse = await store.api.get(
            '/combos/get-active-combo'
          );

          // Validate response
          if (!activeComboResponse?.data) {
            throw new Error('Invalid response from get-active-combo endpoint');
          }

          combos = Array.isArray(activeComboResponse.data.combos)
            ? activeComboResponse.data.combos
            : [];
          selectedCombo = activeComboResponse.data.selectedCombo || null;
          modelIds = selectedCombo?.llms || [];

          // Step 4: Sync default combos to ensure consistency
          // Run in background to avoid blocking UI
          store.api.post('/combos/sync-default-combos').catch((err) => {
            console.error('Background sync of default combos failed:', err);
          });
        }

        // Only proceed if we have valid data from an endpoint
        if (!endpointCalled || !Array.isArray(combos)) {
          return;
        }

        // Update combos in the store - only if we have valid data
        useChatStore.getState().setCombos(combos);

        // Set selected combo
        if (selectedCombo) {
          useChatStore.getState().setSelectedCombo(selectedCombo);
        }

        // Important: Map model IDs to actual model objects from allModels
        if (
          Array.isArray(modelIds) &&
          modelIds.length > 0 &&
          Array.isArray(allModels) &&
          allModels.length > 0
        ) {
          // Create a map for faster lookups
          const modelsMap = {};
          allModels.forEach((model) => {
            if (model && model._id) {
              // Store as string key for easier lookups
              const idString =
                typeof model._id === 'string'
                  ? model._id
                  : model._id.toString();
              modelsMap[idString] = { ...model }; // Use a copy to avoid reference issues
            }
          });

          // Use the map to find models by ID with validation
          const selectedModelsList = modelIds
            .filter((id) => id) // Filter out null/undefined IDs
            .map((id) => {
              const modelId = typeof id === 'string' ? id : id.toString();
              return modelsMap[modelId] || null;
            })
            .filter(
              (model) => model && model._id && model.abbreviation && model.name
            )
            .sort((a, b) =>
              (a.abbreviation || '').localeCompare(b.abbreviation || '')
            );

          if (selectedModelsList.length > 0) {
            setSelectedModels(selectedModelsList);
          } else {
            console.warn('No valid models found matching the selected combo');
          }
        }
      } catch (error) {
        console.error('Error initializing combos:', error);
      }
    };

    initCombos();
  }, [allModels, store]);

  useEffect(() => {
    localStorage.setItem('totalRewards', null);
  }, []);

  useEffect(() => {
    redirectEvent();
  }, [currentEvent]);

  // Connect to general room for a user - Email is the room id
  useEffect(() => {
    const local = localStorage.getItem('profile');
    const profile = JSON.parse(local);
    if (profile) store.subscribeRoom(profile.email);
    if (profile) {
      // userGuiding credentials
      window?.userGuiding?.identify(profile.customerId, {
        email: profile.email,
        name: profile.fname + ' ' + profile.lname,
        plan: profile.plan,
        scope: profile.scope,
        grandscope: profile.grandscope,
        created_at: formatDate(profile.created),
        count_chats: profile.count_chats,
        count_templates: profile.count_templates,
        count_attachments: profile.count_attachments,
        verified: profile.verified,
        coins: profile.credits,
        welcome_use_one: profile.welcome_use_one,
        welcome_use_two: profile.welcome_use_two,
        welcome_use_three: profile.welcome_use_three,
        welcome_use_other: profile.welcome_use_other,
        welcome_who_item: profile.welcome_who_item,
        welcome_who_size: profile.welcome_who_size,
        welcome_how_discovered: profile.welcome_how_discovered,
        welcome_how_discovered_other: profile.welcome_how_discovered_other,
      });
    }

    // Modal to use UG in desktop
    const showModal = localStorage.getItem('modalMobileUG');
    if (profile) {
      if (profile.beta.kind_of_work && width <= 1024 && !showModal) {
        setShowModalTutos(true);
        localStorage.setItem('modalMobileUG', 'true');
      }
    }
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');

    return `${year}/${month}/${day}`;
  };

  const redirectEvent = () => {
    switch (currentEvent.name) {
      case EVENT_NEW_CHAT:
      case EVENT_SELECT_CHAT:
      case SCRIPT_FOR_TTS:
      case REMOVE_MODEL:
        if (location.pathname !== '/chat') navigate('/chat');
        break;
      default:
        break;
    }
  };

  const { open: openComingSoonModal } = useComingSoonModal();

  // Add a debug effect to monitor selectedModels changes
  useEffect(() => {
    const selectedModels = useChatStore.getState().selectedModels;
    // Check if they have all required properties
    if (selectedModels.length > 0) {
      const hasRequiredProps = selectedModels.every(
        (model) => model && model._id && model.abbreviation && model.name
      );

      if (!hasRequiredProps) {
        console.warn(
          'Some models are missing required properties:',
          selectedModels.filter(
            (m) => !m || !m._id || !m.abbreviation || !m.name
          )
        );
      }
    }
  }, [useChatStore((state) => state.selectedModels)]);

  const PagesWithoutHeader = !isMobile ? ['/gallery'] : [];
  return (
    <ChatMessagesProvider>
      <ChatProvider>
        <div className="flex-1 flex w-full h-full">
          {isMobile && mobilePowerPanel ? (
            <>
              <MobilePowerPanel onClose={() => setMobilePowerPanel(false)} />
            </>
          ) : (
            <>
              <PowerPanel />
            </>
          )}
          <div className="flex-1 h-full w-full flex flex-col relative">
            <div
              className={`w-full px-4 absolute top-0 left-0 z-[60] ${
                !active && 'hidden'
              }`}
            >
              <SmartMergeDialog />
            </div>
            {!PagesWithoutHeader?.includes(location.pathname) && (
              <Header open={open} onOpen={() => setOpen(true)} />
            )}
            {outlet}
          </div>
        </div>

        {openComingSoonModal && <ComingSoonModal />}
        {store.profile.continue !== 'beta' && <Welcome />}
        {showModalTutos && (
          <ModalMobileUG
            onContinue={() => setShowModalTutos(false)}
            onClose={() => setShowModalTutos(false)}
          />
        )}
      </ChatProvider>
    </ChatMessagesProvider>
  );
};
export default inject('store')(observer(AuthLayout));
